import type { CSSProperties, ReactNode } from 'react'

import type { IconName } from '@molecule/app-icons'
import { getClassMap } from '@molecule/app-ui'

import { Icon } from './Icon.js'

/**
 * The ONE notice treatment for the workspace modals (Environment, Storage):
 * a leading tone-colored SVG glyph from the bonded icon set, a subtle tinted
 * background + border mixed from the matching semantic theme var
 * (`--color-info` / `--color-warning` / `--color-error` — all defined in
 * `index.css` with `--mol-color-*` fallbacks), and an optional right-aligned
 * action (which wraps below the text on narrow widths).
 *
 * **Boxes are for attention, plain lines are for explanation.** PanelNotice
 * is reserved for the few messages that must stand out: the guest signup
 * callout, the admin-required note, real error states, and the
 * sandbox-not-running warning. Every merely *explanatory* message (managed
 * explainers, capture/coming-soon/why-BYO hints, ask-Synthase pointers,
 * notConfigured explorer states) renders as a {@link MutedNote} instead — one
 * quiet muted line, no icon, no border, no background — so the panels read
 * calm instead of cluttered.
 *
 * Colors come exclusively from CSS theme vars (never a hex literal), so the
 * notice reads correctly in light AND dark themes. Tiny per-secret inline
 * captions (broker-provided/auto/provisioned lines inside SecretRow) are NOT
 * notices and keep their own quiet rendering.
 */

/**
 * Semantic tone of a {@link PanelNotice}. `primary` derives from
 * `--color-primary` exactly like the others derive from their tokens — used
 * for auth/CTA nudges whose action Button is primary blue, so the box and its
 * button share one color (a teal box hosting a blue button "looks odd").
 */
export type PanelNoticeTone = 'info' | 'warning' | 'error' | 'primary'

/** Default bonded-icon glyph per tone (all verified to exist in the set). */
const TONE_ICONS: Record<PanelNoticeTone, IconName> = {
  info: 'info-circle',
  warning: 'exclamation-triangle',
  error: 'x-circle',
  // The sign-in glyph: the primary tone exists for auth nudges (guest signup).
  primary: 'sign-in',
}

/**
 * One panel-level notice.
 * @param root0 - Component props.
 * @param root0.tone - Semantic tone (drives the icon, tint, and border).
 * @param root0.icon - Optional bonded-icon glyph overriding the tone default.
 * @param root0.molId - Automation id for the notice container.
 * @param root0.children - The message.
 * @param root0.action - Optional right-aligned action (e.g. a CTA Button).
 * @returns The rendered notice.
 */
export function PanelNotice({
  tone,
  icon,
  molId,
  children,
  action,
}: {
  tone: PanelNoticeTone
  icon?: IconName
  molId: string
  children: ReactNode
  action?: ReactNode
}) {
  return (
    <div
      data-mol-id={molId}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        flexWrap: 'wrap',
        gap: 8,
        padding: '10px 12px',
        fontSize: 13,
        lineHeight: 1.5,
        color: 'var(--color-foreground)',
        background: `color-mix(in srgb, var(--color-${tone}) 8%, transparent)`,
        border: `1px solid color-mix(in srgb, var(--color-${tone}) 35%, transparent)`,
        borderRadius: 8,
        marginBottom: 10,
      }}
    >
      {/* Tone-colored glyph, top-aligned with the first text line. The
          `--color-<tone>-text` vars (75% tone mixed with the foreground) keep
          contrast in both themes; info has no -text var and falls back to the
          raw tone var. */}
      <span
        aria-hidden="true"
        style={{
          display: 'inline-flex',
          flexShrink: 0,
          marginTop: 2,
          color: `var(--color-${tone}-text, var(--color-${tone}))`,
        }}
      >
        <Icon name={icon ?? TONE_ICONS[tone]} size={15} />
      </span>
      <div style={{ flex: 1, minWidth: 160, wordBreak: 'break-word' }}>{children}</div>
      {action && (
        <div
          style={{
            marginLeft: 'auto',
            flexShrink: 0,
            display: 'inline-flex',
            alignItems: 'center',
          }}
        >
          {action}
        </div>
      )}
    </div>
  )
}

/**
 * The quiet counterpart to {@link PanelNotice}: ONE muted plain-text line for
 * explanatory copy — managed-by-molecule stories, capture notes, coming-soon
 * hints, why-BYO reasons, ask-Synthase pointers, notConfigured explorer
 * states. Deliberately NO icon, NO border, NO background: explanation should
 * never compete with the content it explains. Shared by the Environment and
 * Storage panels so every explanatory line reads identically (12px,
 * `cm.textMuted`, 10px block rhythm).
 * @param root0 - Component props.
 * @param root0.molId - Automation id for the line.
 * @param root0.children - The message.
 * @param root0.style - Optional style overrides (e.g. tightening the margin).
 * @returns The rendered line.
 */
export function MutedNote({
  molId,
  children,
  style,
}: {
  molId: string
  children: ReactNode
  style?: CSSProperties
}) {
  const cm = getClassMap()
  return (
    <div
      data-mol-id={molId}
      className={cm.textMuted}
      style={{ fontSize: 12, lineHeight: 1.5, marginBottom: 10, ...style }}
    >
      {children}
    </div>
  )
}
